import type { IbhShapeResponseDto } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { IbhMaterialResponseDtoProcedureEnum, IbhShape } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import type { MAIN_IBH_INFO_PAGE_PATH_LOCALES } from '@hub/libs/constants';
import { MAIN_IBH_INFO_PAGE_PATH, WIDEN_CDN_IMAGE_BASE_URL } from '@hub/libs/constants';
import type { ProductFragmentAttributes } from '../bearing-hub/ProductDetails/AkeneoProductData/attribute.helper';
import type { ShapeDescriptor } from '@hub/bearing-hub/common/shapes/shapes';
import type { DiameterValue, IbhDimensions } from '@hub/bearing-hub/common/dimensions/dimensions';
import { bevelKey as bevelKeys, dimensionKey as dimensionKeys } from '@hub/bearing-hub/common/dimensions/dimensions';
import type { i18n } from 'i18next';
import { Locale } from '@formatjs/intl-locale';
import { ForceCode, LengthCode, PressureCode, TemperatureCode } from '@icommerce/units';
import { round10 } from '@igus/kopla-domain';
import type {
  IbhEasyClipDimensionsDto,
  IbhFlangeBearingDimensionsDto,
  IbhPistonRingDimensionsDto,
  IbhSleeveBearingDimensionsDto,
  IbhThrustWasherDimensionsDto,
  IbhTwoHoleFlangeBearingPreloadDimensionsDto,
} from '@igus/iglidur-bearing-hub-api';
import { defaultLocale, generateFallbackLng, supportedLocales } from '@icommerce/i18n';

export interface IbhProduct {
  id?: string;
  title?: string;
  usp?: string;
  singleUsp?: string;
  description?: string;
  technicalDataEntityKey?: string;
  onlineTools?: string[];
  innerDiameter?: DiameterValue;
  attributes?: ProductFragmentAttributes;
}

export type IbhCategoryShapeName =
  | 'sleeve_bearing'
  | 'sleeve_bearing_flange'
  | 'flange_bearings'
  | 'thrust_washers'
  | 'piston_rings'
  | 'split_bearings'
  | 'split_bearings_with_anti_rotation_feature'
  | 'easy_clip'
  | 'pre_loaded_two_hole_flanged_bearings'
  | 'disc_springs'
  | 'clip_bearings'
  | 'clip_in_double_flanged_bearings'
  | 'clearance_free_prestressed_bearing_with_flange'
  | 'double_flanged_bearings'
  | 'clearance_free_preloaded_bearings'
  | 'sleeve_bearings_with_flange_and_felt_seal';

export const SUPPORTED_IBH_CATEGORY_KEYS: Array<IbhCategoryShapeName | 'bearings'> = [
  'bearings',
  'sleeve_bearing',
  'sleeve_bearing_flange',
  'thrust_washers',
  'flange_bearings',
  'split_bearings',
  'split_bearings_with_anti_rotation_feature',
  'piston_rings',
  'easy_clip',
  'pre_loaded_two_hole_flanged_bearings',
  'disc_springs',
  'clip_bearings',
  'clip_in_double_flanged_bearings',
  'clearance_free_prestressed_bearing_with_flange',
  'double_flanged_bearings',
  'clearance_free_preloaded_bearings',
  'sleeve_bearings_with_flange_and_felt_seal',
];

export const SUPPORTED_IBH_LANGUAGES: Array<string> = [
  'en',
  'de',
  'fr',
  'es',
  'it',
  'pl',
  'pt',
  'nl',
  'sv',
  'cs',
  'ro',
  'sl',
  'da',
  'hu',
  'id',
  'th',
  'ja',
  'ko',
  'tr',
  'vi',
  'zh',
];
export const SUPPORTED_IGLIDE_IBH_LOCALES = ['en-US', 'en-CA', 'es-MX'];

export const BLACKLISTED_IBH_LOCALES_FOR_PROD = [];

export const COUNTRIES_WITH_SPECIFIC_ARTICLE_NUMBER = ['en-US', 'en-CA'];
export const FORMER_SKUS = {
  // iglide W300
  WSI: 'LSI',
  WSM: 'LSM',
  WFI: 'LFI',
  WFM: 'LFM',
  WTI: 'LTI',
  WTM: 'LTM',
  LSI: 'WSI',
  LSM: 'WSM',
  LFI: 'WFI',
  LFM: 'WFM',
  LTI: 'WTI',
  LTM: 'WTM',

  // iglide X
  XSI: 'TSI',
  XSM: 'TSM',
  XFI: 'TFI',
  XFM: 'TFM',
  XTI: 'TTI',
  XTM: 'TTM',
  XFL: 'TFL',
  TSI: 'XSI',
  TSM: 'XSM',
  TFI: 'XFI',
  TFM: 'XFM',
  TTI: 'XTI',
  TTM: 'XTM',
  TFL: 'XFL',
};

export const SUPPORTED_IBH_LOCALES = [
  // .eu locales
  'en',
  'es',
  'fr',

  // english locales
  'en-GB',
  'en-IE',
  'en-AU',
  'en-CA',
  'en-EE',
  'en-EG',
  'en-FI',
  'en-GR',
  'en-IL',
  'en-IN',
  // 'en-LB', // NOK -> not available online
  'en-LT',
  'en-MY',
  // 'en-NG', // NOK -> not available online
  'en-NO',
  'en-NZ',
  'en-SG',
  'en-US',
  'en-ZA',

  // spanish locales
  'es-ES',
  'es-AR',
  'es-CL',
  'es-MX',

  // german locales
  'de-DE',
  'de-AT',
  'de-CH',
  'de-BE',

  // french locales
  'fr-FR',
  'fr-CH',
  'fr-CA',
  'fr-BE',
  // 'fr-TN', // NOK -> not available online

  // italian locales
  'it-IT',
  'it-CH',

  // bulgarian locale
  'bg-BG',

  // polish locale
  'pl-PL',

  // danish locale
  'da-DK',

  // latvian locale
  'lv-LV',

  // serbian locale
  'sr-RS',

  // czech locales
  'cs-CZ',
  'cs-SK',

  // slovak locale
  'sk-SK',

  // croatian locale
  'hr-HR',

  // hungarian locale
  'hu-HU',

  // indonesian locale
  'id-ID',

  // japanese locale
  'ja-JP',

  // korean locale
  'ko-KR',

  // dutch locales
  'nl-BE',
  'nl-NL',

  // portugese locales
  'pt-PT',
  'pt-BR',

  // romanian locale
  'ro-RO',

  // slovenian locale
  'sl-SI',

  // swedish locale
  'sv-SE',

  // thai locale
  'th-TH',

  // turkish locale
  'tr-TR',

  // vietnamese locale
  'vi-VN',

  // chinese locales
  'zh-CN',
  'zh-TW',

  // ukranian locale
  'ru-UA',
];
export const SUPPORTED_IBH_MULTILANGUAGE_LOCALES = ['es', 'fr', 'fr-BE', 'de-BE', 'fr-CA', 'fr-CH', 'it-CH'];

type SUPPORTED_IBH_LOCALES_VALUES = {
  [K in (typeof SUPPORTED_IBH_LOCALES)[number]]: string[];
};

const deOldPaths = ['gleitlager-weitere-produkte', 'gleitlager-weitere-bauformen', 'gleitlager'];
const esOldPaths = ['more-products', 'cojinetes-especiales', 'cojinetes-de-plastico'];
const enOldPaths = ['more-products', 'bearings-other-designs', 'plain-bearing'];
const enUsOldPaths = ['more-iglide-products', 'specially-designed-bearings', 'plain-bearing']; // NOK -> not supported yet (webtest only)
const plOldPaths = ['more-products', 'bearings-other-designs', 'lozyska-slizgowe'];
const nlOldPaths = ['more-products', 'bearings-other-designs', 'kunststof-glijlagers'];

// List of paths having no direct target shape
export const OLD_IGLIDUR_PATHS_WITH_NO_TARGET: SUPPORTED_IBH_LOCALES_VALUES = {
  en: enOldPaths,
  'en-GB': enOldPaths,
  'en-IE': enOldPaths,
  'en-AU': enOldPaths, // NOK -> not supported yet (webtest only)
  'en-BG': enOldPaths,
  'en-CA': enOldPaths,
  'en-EE': enOldPaths,
  'en-EG': enOldPaths,
  'en-FI': enOldPaths,
  'en-GR': enOldPaths,
  'en-HR': enOldPaths,
  'en-IL': enOldPaths,
  'en-IN': enOldPaths,
  // 'en-LB': enOldPaths, // NOK -> not available online
  'en-LT': enOldPaths,
  'en-MY': enOldPaths,
  // 'en-NG': enOldPaths, // NOK -> not available online
  'en-NO': enOldPaths,
  'en-NZ': enOldPaths, // NOK -> not supported yet (webtest only)
  'en-RS': enOldPaths,
  'en-SG': enOldPaths,
  'en-SI': enOldPaths,
  'en-US': enUsOldPaths,
  'en-ZA': enOldPaths,

  es: enOldPaths,
  'es-ES': esOldPaths,
  'es-AR': enOldPaths,
  'es-CL': enOldPaths,
  'es-MX': enOldPaths,

  'de-DE': deOldPaths,
  'de-AT': deOldPaths,
  'de-CH': deOldPaths,
  'de-BE': deOldPaths,

  fr: enOldPaths,
  'fr-FR': enOldPaths,
  'fr-CH': enOldPaths,
  'fr-BE': enOldPaths,
  'fr-CA': enOldPaths,
  // 'fr-TN': enOldPaths, // NOK -> not available online

  'it-CH': enOldPaths,
  'it-IT': enOldPaths,

  'pl-PL': plOldPaths,

  'da-DK': enOldPaths,

  'cs-CZ': enOldPaths,
  'cs-SK': enOldPaths,

  // 'hr-HR': enOldPaths, // NOK -> not available online

  'hu-HU': enOldPaths,

  'id-ID': enOldPaths,

  'ja-JP': enOldPaths,

  'ko-KR': enOldPaths,

  'nl-NL': nlOldPaths,
  'nl-BE': enOldPaths,

  'pt-PT': enOldPaths,
  'pt-BR': enOldPaths,

  'ro-RO': enOldPaths,

  'sl-SI': enOldPaths,

  'sv-SE': enOldPaths,

  'th-TH': enOldPaths,

  'tr-TR': enOldPaths,

  'vi-VN': enOldPaths,

  // 'zh-CN': enOldPaths, // NOK -> not supported yet
  'zh-TW': enOldPaths,

  'ru-UA': enOldPaths,
};

const dePaths = {
  S: {
    path: 'zylindrische-gleitlager',
    oldIglidurPaths: ['zylindrisches-gleitlager'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearing'),
  },
  F: {
    path: 'zylindrische-gleitlager-mit-bund',
    oldIglidurPaths: ['zylindrisches-gleitlager-mit-bund'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearing_flange'),
  },
  FL: {
    path: 'flanschgleitlager',
    oldIglidurPaths: ['flanschgleitlager'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('flange_bearings'),
  },
  T: {
    path: 'anlaufscheiben',
    oldIglidurPaths: ['anlaufscheibe'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('thrust_washers'),
  },
  PR: {
    path: 'fuehrungsringe',
    oldIglidurPaths: ['fuehrungsring'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('piston_rings'),
  },
  C: {
    path: 'clipslager',
    oldIglidurPaths: ['clips-gleitlager'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clip_bearings'),
  },
  Y: {
    path: 'schlitzlager',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('split_bearings'),
  },
  YK: {
    path: 'schlitzlager-mit-verdrehsicherung',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('split_bearings_with_anti_rotation_feature'),
  },
  EC: {
    path: 'easy-clip-gleitlager',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('easy_clip'),
  },
  VFL: {
    path: 'flanschgleitlager-mit-vorspannung',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('pre_loaded_two_hole_flanged_bearings'),
  },
  TE: {
    path: 'tellerfedern',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('disc_springs'),
  },
  D: {
    path: 'doppelbundlager',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clip_in_double_flanged_bearings'),
  },
  VF: {
    path: 'spielfrei-vorgespannte-lager-mit-bund',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clearance_free_prestressed_bearing_with_flange'),
  },
  K: {
    path: 'doppelbundlager-mit-bund',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('double_flanged_bearings'),
  },
  VS: {
    path: 'spielfrei-vorgespannte-lager',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clearance_free_preloaded_bearings'),
  },
  FSG: {
    path: 'gleitlager-mit-bund-und-filzdichtung',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearings_with_flange_and_felt_seal'),
  },
};

const enPaths = {
  S: {
    path: 'sleeve-bearings',
    oldIglidurPaths: ['cylindrical-bearing', 'sleeve-bearings'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearing'),
  },
  F: {
    path: 'flanged-bearings',
    oldIglidurPaths: ['sleeve-bearing-with-flange'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearing_flange'),
  },
  FL: {
    path: 'two-hole-flange-bearings',
    oldIglidurPaths: ['flange-bearing'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('flange_bearings'),
  },
  T: {
    path: 'thrust-washers',
    oldIglidurPaths: ['thrust-washer'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('thrust_washers'),
  },
  PR: {
    path: 'guide-rings',
    oldIglidurPaths: ['guide-rings'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('piston_rings'),
  },
  C: {
    path: 'clip-bearings',
    oldIglidurPaths: ['clip-on-plain-bearing'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clip_bearings'),
  },
  Y: {
    path: 'split-bearings',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('split_bearings'),
  },
  YK: {
    path: 'anti-rotation-split-bearings',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('split_bearings_with_anti_rotation_feature'),
  },
  EC: {
    path: 'easy-clips',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('easy_clip'),
  },
  VFL: {
    path: 'two-hole-flange-bearings-with-preload',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('pre_loaded_two_hole_flanged_bearings'),
  },
  TE: {
    path: 'disc-springs',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('disc_springs'),
  },
  D: {
    path: 'clip-in-double-flanged-bearings',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clip_in_double_flanged_bearings'),
  },
  VF: {
    path: 'pre-tensioned-flanged-bearings',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clearance_free_prestressed_bearing_with_flange'),
  },
  K: {
    path: 'double-flanged-bearings',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('double_flanged_bearings'),
  },
  VS: {
    path: 'pre-loaded-bearings',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clearance_free_preloaded_bearings'),
  },
  FSG: {
    path: 'sleeve-bearings-with-flange-and-felt-seal',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearings_with_flange_and_felt_seal'),
  },
};

const enUsPaths = {
  S: {
    path: 'sleeve-bearings',
    oldIglidurPaths: ['cylindrical-bearing', 'sleeve-bearings'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearing'),
  },
  F: {
    path: 'flange-bearings',
    oldIglidurPaths: ['sleeve-bearing-with-flange'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearing_flange'),
  },
  FL: {
    path: 'two-bolt-flange-bearings',
    oldIglidurPaths: ['flange-housing-bearings', 'flange-bearing'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('flange_bearings'),
  },
  T: {
    path: 'thrust-washers',
    oldIglidurPaths: ['thrust-washer'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('thrust_washers'),
  },
  PR: {
    path: 'wear-rings',
    oldIglidurPaths: ['guide-rings', 'piston-ring'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('piston_rings'),
  },
  C: {
    path: 'clip-bearings-double-flange',
    oldIglidurPaths: ['clip-on-plain-bearing'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clip_bearings'),
  },
  Y: {
    path: 'clip-bearings-single-flange',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('split_bearings'),
  },
  YK: {
    path: 'anti-rotation-clip-bearings',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('split_bearings_with_anti_rotation_feature'),
  },
  EC: {
    path: 'easy-clips',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('easy_clip'),
  },
  VFL: {
    path: 'two-bolt-flange-bearings-with-preload',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('pre_loaded_two_hole_flanged_bearings'),
  },
  TE: {
    path: 'polysorb-disc-springs',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('disc_springs'),
  },
  D: {
    path: 'clip-in-double-flanged-bearings',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clip_in_double_flanged_bearings'),
  },
  VF: {
    path: 'clearance-free-preloaded-flange-bearings',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clearance_free_prestressed_bearing_with_flange'),
  },
  K: {
    path: 'flange-bearings-pressed-in-and-fold-down',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('double_flanged_bearings'),
  },
  VS: {
    path: 'clearance-free-preloaded-sleeve-bearings',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clearance_free_preloaded_bearings'),
  },
  FSG: {
    path: 'sleeve-bearings-with-flange-and-felt-seal',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearings_with_flange_and_felt_seal'),
  },
};

const plPaths = {
  S: {
    path: 'tuleje-slizgowe',
    oldIglidurPaths: ['tuleje-cylindryczne', 'cylindrical-bearing'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearing'),
  },
  F: {
    path: 'lozyska-kolnierzowe',
    oldIglidurPaths: ['sleeve-bearing-with-flange', 'sleeve-bearings-with-flange'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearing_flange'),
  },
  FL: {
    path: 'dwuotworowe-lozyska-kolnierzowe',
    oldIglidurPaths: ['lozyska-cylindryczne-kolnierzowe'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('flange_bearings'),
  },
  T: {
    path: 'podkladka-oporowa',
    oldIglidurPaths: ['podkladki-oporowe', 'thrust-washer', 'thrust-washers'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('thrust_washers'),
  },
  PR: {
    path: 'pierscienie-prowadzace',
    oldIglidurPaths: ['pierscienie-tlokowe', 'guide-ring', 'guide-rings'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('piston_rings'),
  },
  C: {
    path: 'lozyska-klipsowe-dwukolnierzowe',
    oldIglidurPaths: ['lozyska-klipsowe'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clip_bearings'),
  },
  Y: {
    path: 'lozyska-dzielone',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('split_bearings'),
  },
  YK: {
    path: 'lozyska-dzielone-z-blokada-obrotu',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('split_bearings_with_anti_rotation_feature'),
  },
  EC: {
    path: 'lozyska-klipsowe-wklikiwane',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('easy_clip'),
  },
  VFL: {
    path: 'wstepnie-obciazone-lozyska-kolnierzowe-z-dwoma-otworami',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('pre_loaded_two_hole_flanged_bearings'),
  },
  TE: {
    path: 'sprezyny-talerzowe',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('disc_springs'),
  },
  D: {
    path: 'lozyska-z-podwojnym-kolnierzem',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clip_in_double_flanged_bearings'),
  },
  VF: {
    path: 'bezluzowe-wstepnie-obciazone-lozysko-kolnierzowe',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clearance_free_prestressed_bearing_with_flange'),
  },
  K: {
    path: 'dwukolnierzowe-lozyska',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('double_flanged_bearings'),
  },
  VS: {
    path: 'wstepnie-obciazone-tuleje-slizgowe',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clearance_free_preloaded_bearings'),
  },
  FSG: {
    path: 'lozysko-tulejowe-z-kolnierzem-i-uszczelka-filcowa',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearings_with_flange_and_felt_seal'),
  },
};

const esPaths = {
  S: {
    path: 'cojinetes-friccion',
    oldIglidurPaths: ['cylindrical-bearing', 'cojinetes-friccion'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearing'),
  },
  F: {
    path: 'casquillos-con-valona',
    oldIglidurPaths: ['sleeve-bearing-with-flange', 'cojinetes-friccion-con-valona'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearing_flange'),
  },
  FL: {
    path: 'cojinetes-friccion-con-brida',
    oldIglidurPaths: ['flange-bearing', 'cojinetes-friccion-con-brida'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('flange_bearings'),
  },
  T: {
    path: 'arandelas-de-plastico',
    oldIglidurPaths: ['thrust-washer', 'arandelas-de-plastico'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('thrust_washers'),
  },
  PR: {
    path: 'aros-de-piston',
    oldIglidurPaths: ['guide-rings'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('piston_rings'),
  },
  C: {
    path: 'cojinetes-de-clip',
    oldIglidurPaths: ['clip-on-plain-bearing', 'cojinetes-de-friccion-de-clip'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clip_bearings'),
  },
  Y: {
    path: 'cojinetes-con-ranura',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('split_bearings'),
  },
  YK: {
    path: 'cojinetes-con-ranura-antirrotacion',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('split_bearings_with_anti_rotation_feature'),
  },
  EC: {
    path: 'cojinetes-de-clip-a-presion',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('easy_clip'),
  },
  VFL: {
    path: 'cojinetes-con-brida-precarga',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('pre_loaded_two_hole_flanged_bearings'),
  },
  TE: {
    path: 'arandelas-de-resorte',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('disc_springs'),
  },
  D: {
    path: 'cojinetes-de-clip-doble-valona',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clip_in_double_flanged_bearings'),
  },
  VF: {
    path: 'cojinetes-con-valona-precarga',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clearance_free_prestressed_bearing_with_flange'),
  },
  K: {
    path: 'cojinetes-atornillados-con-doble-valona',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('double_flanged_bearings'),
  },
  VS: {
    path: 'cojinetes-con-precarga',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clearance_free_preloaded_bearings'),
  },
  FSG: {
    path: 'casquillo-con-valona-y-sello-de-fieltro',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearings_with_flange_and_felt_seal'),
  },
};

const frPaths = {
  S: {
    path: 'paliers-lisses-cylindriques',
    oldIglidurPaths: ['cylindrical-bearing'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearing'),
  },
  F: {
    path: 'paliers-lisses-a-collerette',
    oldIglidurPaths: ['sleeve-bearing-with-flange'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearing_flange'),
  },
  FL: {
    path: 'paliers-appliques',
    oldIglidurPaths: ['flange-bearing'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('flange_bearings'),
  },
  T: {
    path: 'rondelles',
    oldIglidurPaths: ['thrust-washer'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('thrust_washers'),
  },
  PR: {
    path: 'bagues-de-guidage',
    oldIglidurPaths: ['guide-rings'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('piston_rings'),
  },
  C: {
    path: 'paliers-clips',
    oldIglidurPaths: ['clip-on-plain-bearing'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clip_bearings'),
  },
  Y: {
    path: 'paliers-fendus',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('split_bearings'),
  },
  YK: {
    path: 'paliers-fendus-avec-anti-rotation',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('split_bearings_with_anti_rotation_feature'),
  },
  EC: {
    path: 'paliers-easy-clip',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('easy_clip'),
  },
  VFL: {
    path: 'paliers-appliques-precontraints',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('pre_loaded_two_hole_flanged_bearings'),
  },
  TE: {
    path: 'rondelles-ressorts',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('disc_springs'),
  },
  D: {
    path: 'paliers-a-double-collerette',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clip_in_double_flanged_bearings'),
  },
  VF: {
    path: 'paliers-precontraints-a-collerette',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clearance_free_prestressed_bearing_with_flange'),
  },
  K: {
    path: 'paliers-a-collerette-avec-rabat',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('double_flanged_bearings'),
  },
  VS: {
    path: 'paliers-lisses-precontraints',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clearance_free_preloaded_bearings'),
  },
  FSG: {
    path: 'palier-lisse-a-collerette-et-joint-en-feutre',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearings_with_flange_and_felt_seal'),
  },
};

const nlPaths = {
  S: {
    path: 'cilindrische-bussen',
    oldIglidurPaths: ['cylindrical-bearing'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearing'),
  },
  F: {
    path: 'flenslager',
    oldIglidurPaths: ['sleeve-bearing-with-flange'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearing_flange'),
  },
  FL: {
    path: 'flenslagers-met-twee-gaten',
    oldIglidurPaths: ['flange-bearing'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('flange_bearings'),
  },
  T: {
    path: 'axiaalschijven',
    oldIglidurPaths: ['thrust-washer'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('thrust_washers'),
  },
  PR: {
    path: 'geleideringen',
    oldIglidurPaths: ['guide-rings'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('piston_rings'),
  },
  C: {
    path: 'klemlagers',
    oldIglidurPaths: ['clip-on-plain-bearing'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clip_bearings'),
  },
  Y: {
    path: 'clip-on-glijlagers',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('split_bearings'),
  },
  YK: {
    path: 'clip-on-glijlagers-met-anti-rotatie',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('split_bearings_with_anti_rotation_feature'),
  },
  EC: {
    path: 'easy-clip-klemlagers',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('easy_clip'),
  },
  VFL: {
    path: 'flenslagers-met-twee-gaten-met-voorspanning',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('pre_loaded_two_hole_flanged_bearings'),
  },
  TE: {
    path: 'belleville-ringen',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('disc_springs'),
  },
  D: {
    path: 'clip-in-lagers-met-dubbele-flens',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clip_in_double_flanged_bearings'),
  },
  VF: {
    path: 'spelingvrije-flenslager-met-voorspanning',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clearance_free_prestressed_bearing_with_flange'),
  },
  K: {
    path: 'lagers-met-dubbele-flen',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('double_flanged_bearings'),
  },
  VS: {
    path: 'cilindrische-bus-met-voorspanning',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clearance_free_preloaded_bearings'),
  },
  FSG: {
    path: 'cilindrische-bus-met-flens-en-viltafdichting',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearings_with_flange_and_felt_seal'),
  },
};

const ptPaths = {
  S: {
    path: 'casquilhos-autolubrificados',
    oldIglidurPaths: ['cylindrical-bearing'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearing'),
  },
  F: {
    path: 'casquilhos-com-flange',
    oldIglidurPaths: ['sleeve-bearing-with-flange'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearing_flange'),
  },
  FL: {
    path: 'casquilhos-com-flange-de-dois-furos',
    oldIglidurPaths: ['flange-bearing'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('flange_bearings'),
  },
  T: {
    path: 'anilhas-planas',
    oldIglidurPaths: ['thrust-washer'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('thrust_washers'),
  },
  PR: {
    path: 'aneis-guia',
    oldIglidurPaths: ['guide-rings', 'piston-ring'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('piston_rings'),
  },
  C: {
    path: 'casquilhos-de-clipe',
    oldIglidurPaths: ['clip-on-plain-bearing'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clip_bearings'),
  },
  Y: {
    path: 'casquilhos-bipartidos',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('split_bearings'),
  },
  YK: {
    path: 'casquilhos-bipartidos-com-anti-rotacao',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('split_bearings_with_anti_rotation_feature'),
  },
  EC: {
    path: 'casquilhos-de-easy-clipe',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('easy_clip'),
  },
  VFL: {
    path: 'casquilhos-com-flange-de-dois-furos-com-pre-carga',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('pre_loaded_two_hole_flanged_bearings'),
  },
  TE: {
    path: 'anilhas-mola',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('disc_springs'),
  },
  D: {
    path: 'casquilhos-de-clipe-com-dupla-flange',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clip_in_double_flanged_bearings'),
  },
  VF: {
    path: 'casquilhos-com-flange-com-pre-carga-e-sem-folga',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clearance_free_prestressed_bearing_with_flange'),
  },
  K: {
    path: 'casquilhos-com-dupla-flange',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('double_flanged_bearings'),
  },
  VS: {
    path: 'casquilhos-com-pre-carga',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clearance_free_preloaded_bearings'),
  },
  FSG: {
    path: 'casquilho-autolubrificado-com-flange-e-vedante-de-feltro',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearings_with_flange_and_felt_seal'),
  },
};

const ptBrPaths = {
  S: {
    path: 'buchas',
    oldIglidurPaths: ['cylindrical-bearing'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearing'),
  },
  F: {
    path: 'buchas-de-flange',
    oldIglidurPaths: ['sleeve-bearing-with-flange'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearing_flange'),
  },
  FL: {
    path: 'mancal-de-flange-com-dois-furos',
    oldIglidurPaths: ['flange-bearing'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('flange_bearings'),
  },
  T: {
    path: 'arruelas-de-pressao',
    oldIglidurPaths: ['thrust-washer'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('thrust_washers'),
  },
  PR: {
    path: 'aneis-guia',
    oldIglidurPaths: ['guide-rings'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('piston_rings'),
  },
  C: {
    path: 'buchas-de-clip',
    oldIglidurPaths: ['clip-on-plain-bearing'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clip_bearings'),
  },
  Y: {
    path: 'buchas-bipartidas',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('split_bearings'),
  },
  YK: {
    path: 'buchas-bipartidas-com-anti-rotacao',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('split_bearings_with_anti_rotation_feature'),
  },
  EC: {
    path: 'buchas-de-easy-clip',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('easy_clip'),
  },
  VFL: {
    path: 'buchas-de-flange-de-dois-furos-com-pre-cargas',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('pre_loaded_two_hole_flanged_bearings'),
  },
  TE: {
    path: 'molas-de-disco',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('disc_springs'),
  },
  D: {
    path: 'buchas-de-clipe-com-flange-dupla',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clip_in_double_flanged_bearings'),
  },
  VF: {
    path: 'buchas-flangeadas-pre-carregadas-sem-folga',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clearance_free_prestressed_bearing_with_flange'),
  },
  K: {
    path: 'buchas-com-flange-dupla',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('double_flanged_bearings'),
  },
  VS: {
    path: 'buchas-com-pre-carga',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clearance_free_preloaded_bearings'),
  },
  FSG: {
    path: 'bucha-autolubrificante-e-vedação-de-feltro',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearings_with_flange_and_felt_seal'),
  },
};

const itPaths = {
  S: {
    path: 'boccole-cilindriche',
    oldIglidurPaths: ['cylindrical-bearing'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearing'),
  },
  F: {
    path: 'boccole-flangiate',
    oldIglidurPaths: ['sleeve-bearing-with-flange'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearing_flange'),
  },
  FL: {
    path: 'flange-forate',
    oldIglidurPaths: ['flange-bearing'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('flange_bearings'),
  },
  T: {
    path: 'ralle-reggispinta',
    oldIglidurPaths: ['thrust-washer'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('thrust_washers'),
  },
  PR: {
    path: 'anelli-guida',
    oldIglidurPaths: ['guide-rings'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('piston_rings'),
  },
  C: {
    path: 'boccole-per-lamiera',
    oldIglidurPaths: ['clip-on-plain-bearing'],
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clip_bearings'),
  },
  Y: {
    path: 'boccole-flangiate-con-spacco-assiale',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('split_bearings'),
  },
  YK: {
    path: 'boccole-flangiate-con-spacco-assiale-anti-rotazione',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('split_bearings_with_anti_rotation_feature'),
  },
  EC: {
    path: 'cuscinetti-easy-clip',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('easy_clip'),
  },
  VFL: {
    path: 'flange-a-due-fori-precaricate',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('pre_loaded_two_hole_flanged_bearings'),
  },
  TE: {
    path: 'molle-a-tazza',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('disc_springs'),
  },
  D: {
    path: 'boccole-a-doppia-flangia',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clip_in_double_flanged_bearings'),
  },
  VF: {
    path: 'boccole-flangiate-con-precarico',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clearance_free_prestressed_bearing_with_flange'),
  },
  K: {
    path: 'boccole-a-doppia-flangia-flessibile',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('double_flanged_bearings'),
  },
  VS: {
    path: 'boccole-con-precarico',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('clearance_free_preloaded_bearings'),
  },
  FSG: {
    path: 'manicotto-flangiato-e-guarnizione-in-feltro',
    supported: SUPPORTED_IBH_CATEGORY_KEYS.includes('sleeve_bearings_with_flange_and_felt_seal'),
  },
};

export const SHAPE_DESCRIPTORS: {
  [key: string]: {
    [key in IbhShape]: {
      path: string;
      oldIglidurPaths?: string[];
      supported: boolean;
    };
  };
} = {
  'de-DE': dePaths,
  'de-AT': dePaths,
  'de-CH': dePaths,
  'de-BE': dePaths,

  en: enPaths,
  'en-GB': enPaths,
  'en-IE': enPaths,
  'en-AU': enPaths, // OK -> but not supported yet
  'en-BG': enPaths,
  'en-CA': enUsPaths,
  'en-EE': enPaths,
  'en-EG': enPaths,
  'en-FI': enPaths,
  'en-GR': enPaths,
  'en-HR': enPaths,
  'en-IL': enPaths,
  'en-IN': enPaths,
  'en-LT': enPaths,
  // 'en-LB': enPaths, // NOK -> not available online
  'en-MY': enPaths,
  // 'en-NG': enPaths, // NOK -> not available online
  'en-NO': enPaths,
  'en-NZ': enPaths,
  'en-RS': enPaths,
  'en-SG': enPaths,
  'en-SI': enPaths,
  'en-US': enUsPaths,
  'en-ZA': enPaths,

  es: esPaths,
  'es-ES': esPaths,
  'es-AR': esPaths,
  'es-CL': esPaths,
  'es-MX': esPaths,

  fr: frPaths,
  'fr-FR': frPaths,
  'fr-CH': frPaths,
  'fr-BE': frPaths,
  'fr-CA': frPaths,
  // 'fr-TN': frPaths, // NOK -> not available online

  'it-IT': itPaths,
  'it-CH': itPaths,

  'pl-PL': plPaths,

  'da-DK': enPaths,

  'lv-LV': enPaths,

  'cs-CZ': enPaths,
  'cs-SK': enPaths,

  // 'hr-HR': enPaths, // NOK -> not available online

  'hu-HU': enPaths,

  'id-ID': enPaths,

  'ja-JP': enPaths,

  'ko-KR': enPaths,

  'nl-BE': nlPaths,
  'nl-NL': nlPaths,

  'pt-PT': ptPaths,
  'pt-BR': ptBrPaths,

  'ro-RO': enPaths,

  'sl-SI': enPaths,

  'sv-SE': enPaths,

  'th-TH': enPaths,

  'tr-TR': enPaths,

  'vi-VN': enPaths,

  'zh-CN': enPaths, // OK -> but not supported yet
  'zh-TW': enPaths,

  'ru-UA': enPaths,
};

export enum BasketParamsShapes {
  EC = 'EasyClipDimensionsDto_Input',
  F = 'FlangeBearingDimensionsDto_Input',
  FL = 'TwoHoleFlangeBearingDimensionsDto_Input',
  C = 'CDimensionsDto_Input',
  Y = 'SplitBearingDimensionsDto_Input',
  YK = 'SplitBearingAntiRotationDimensionsDto_Input',
  PR = 'PistonRingDimensionsDto_Input',
  S = 'SleeveBearingDimensionsDto_Input',
  T = 'ThrustWasherDimensionsDto_Input',
  VFL = 'TwoHoleFlangeBearingPreloadDimensionsDto_Input',
  TE = 'TeDimensionsDto_Input',
  D = 'DDimensionsDto_Input',
  VF = 'VfDimensionsDto_Input',
  K = 'KDimensionsDto_Input',
  VS = 'VsDimensionsDto_Input',
  FSG = 'FsgDimensionsDto_Input',
}

export enum SortingState {
  NoSort = 'no_sort',
  Ascending = 'ascending',
  Descending = 'descending',
}
export const distanceFactor = 1 / 25.4; // 0,03937007874
export const forceFactor = 0.2248089439;
export const areaFactor = distanceFactor * distanceFactor; // 0,0015500031
export const pressureFactor = forceFactor / areaFactor;

const fallbackLngConfig = generateFallbackLng(supportedLocales);

export class BearingHubHelper {
  static getShapeByShapePath(shapePath: string, locale: string, shapes: IbhShapeResponseDto[]) {
    const shape = this.getShapeDescriptors(locale, shapes).find(shapeItem => shapeItem.shapePath === shapePath);

    if (shape && shape.shapeKey) {
      return shape;
    }

    return null;
  }

  static getShapeDescriptors(locale: string, shapes: IbhShapeResponseDto[]): ShapeDescriptor[] {
    let currentDescriptors = SHAPE_DESCRIPTORS[locale];
    if (!currentDescriptors) {
      currentDescriptors = SHAPE_DESCRIPTORS[defaultLocale];
    }

    return shapes
      .map(shape => {
        if (currentDescriptors) {
          const foundShapeDescriptor = currentDescriptors;

          return {
            shapeKey: shape.shape,
            widenShortKeyIcon: shape.widen?.iconShortKey || null,
            widenShortKeySchema: shape.widen?.schemaShortKey || null,
            shapePath: foundShapeDescriptor[shape.shape]?.path || null,
            supported: foundShapeDescriptor[shape.shape]?.supported || null,
          } as ShapeDescriptor;
        }

        return null;
      })
      .filter(Boolean);
  }

  static getDimensionsParam = (dimensions: IbhDimensions | null, shape: ShapeDescriptor, skipUnderscore = false) => {
    if (!dimensions) {
      return '';
    }

    switch (shape.shapeKey) {
      case IbhShape.S: {
        const sleeveBearingDimensions = dimensions as IbhSleeveBearingDimensionsDto;
        return `${!skipUnderscore ? '_' : ''}${Object.keys(sleeveBearingDimensions)
          .filter(key => key !== '__typename')
          .map(key =>
            sleeveBearingDimensions[key as keyof IbhSleeveBearingDimensionsDto]
              ? `${key}:${sleeveBearingDimensions[key as keyof IbhSleeveBearingDimensionsDto]}`
              : null
          )
          .filter(item => !!item)
          .join('-')}`;
      }
      case IbhShape.F: {
        const flangeBearingDimensions = dimensions as IbhFlangeBearingDimensionsDto;
        return `${!skipUnderscore ? '_' : ''}${Object.keys(flangeBearingDimensions)
          .filter(key => key !== '__typename')
          .map(key =>
            flangeBearingDimensions[key as keyof IbhFlangeBearingDimensionsDto]
              ? `${key}:${flangeBearingDimensions[key as keyof IbhFlangeBearingDimensionsDto]}`
              : null
          )
          .filter(item => !!item)
          .join('-')}`;
      }
      case IbhShape.FL: {
        const twoHoleFlangeBearingDimensions = dimensions as IbhTwoHoleFlangeBearingPreloadDimensionsDto;
        return `${!skipUnderscore ? '_' : ''}${Object.keys(twoHoleFlangeBearingDimensions)
          .filter(key => key !== '__typename')
          .map(key =>
            twoHoleFlangeBearingDimensions[key as 'd1'] ? `${key}:${twoHoleFlangeBearingDimensions[key as 'd1']}` : null
          )
          .filter(item => !!item)
          .join('-')}`;
      }
      case IbhShape.T: {
        const thrustWasherDimensions = dimensions as IbhThrustWasherDimensionsDto;
        return `${!skipUnderscore ? '_' : ''}${Object.keys(thrustWasherDimensions)
          .filter(key => key !== '__typename')
          .map(key =>
            thrustWasherDimensions[key as keyof IbhThrustWasherDimensionsDto]
              ? `${key}:${thrustWasherDimensions[key as keyof IbhThrustWasherDimensionsDto]}`
              : null
          )
          .filter(item => !!item)
          .join('-')}`;
      }
      case IbhShape.EC: {
        const easyClipDimensions = dimensions as IbhEasyClipDimensionsDto;
        return `${!skipUnderscore ? '_' : ''}${Object.keys(easyClipDimensions)
          .filter(key => key !== '__typename')
          .map(key =>
            easyClipDimensions[key as keyof IbhEasyClipDimensionsDto]
              ? `${key}:${easyClipDimensions[key as keyof IbhEasyClipDimensionsDto]}`
              : null
          )
          .filter(item => !!item)
          .join('-')}`;
      }
      case IbhShape.C: {
        const cDimensions = dimensions as IbhEasyClipDimensionsDto;
        return `${!skipUnderscore ? '_' : ''}${Object.keys(cDimensions)
          .filter(key => key !== '__typename')
          .map(key =>
            cDimensions[key as keyof IbhEasyClipDimensionsDto]
              ? `${key}:${cDimensions[key as keyof IbhEasyClipDimensionsDto]}`
              : null
          )
          .filter(item => !!item)
          .join('-')}`;
      }
      case IbhShape.PR: {
        const pistonRingDimensions = dimensions as IbhPistonRingDimensionsDto;
        return `${!skipUnderscore ? '_' : ''}${Object.keys(pistonRingDimensions)
          .filter(key => key !== '__typename')
          .map(key =>
            pistonRingDimensions[key as keyof IbhPistonRingDimensionsDto].toString()
              ? `${key}:${pistonRingDimensions[key as keyof IbhPistonRingDimensionsDto]}`
              : null
          )
          .filter(item => !!item)
          .join('-')}`;
      }
      default:
        return '';
    }
  };

  static getShapeKeyFromCategoryName(shapeName: IbhCategoryShapeName): { key: IbhShape } {
    const keyMap: {
      [key in IbhCategoryShapeName]: {
        key: IbhShape;
      };
    } = {
      sleeve_bearing: {
        key: IbhShape.S,
      },
      sleeve_bearing_flange: {
        key: IbhShape.F,
      },
      flange_bearings: {
        key: IbhShape.FL,
      },
      thrust_washers: {
        key: IbhShape.T,
      },
      piston_rings: {
        key: IbhShape.PR,
      },
      clip_bearings: {
        key: IbhShape.C,
      },
      split_bearings: {
        key: IbhShape.Y,
      },
      split_bearings_with_anti_rotation_feature: {
        key: IbhShape.YK,
      },
      easy_clip: {
        key: IbhShape.EC,
      },
      pre_loaded_two_hole_flanged_bearings: {
        key: IbhShape.VFL,
      },
      disc_springs: {
        key: IbhShape.TE,
      },
      clip_in_double_flanged_bearings: {
        key: IbhShape.D,
      },
      clearance_free_prestressed_bearing_with_flange: {
        key: IbhShape.VF,
      },
      double_flanged_bearings: {
        key: IbhShape.K,
      },
      clearance_free_preloaded_bearings: {
        key: IbhShape.VS,
      },
      sleeve_bearings_with_flange_and_felt_seal: {
        key: IbhShape.FSG,
      },
    };
    return keyMap[shapeName];
  }

  static convertTemperatureUnit(
    { value, unit }: { value: number; unit?: TemperatureCode },
    toUnit: TemperatureCode
  ): number {
    if (unit === toUnit) {
      return value;
    }
    if (toUnit === TemperatureCode.FAHRENHEIT) {
      return round10((value * 9) / 5 + 32);
    }

    return round10((5 / 9) * (value - 32));
  }

  static convertForceUnit({ value, unit }: { value: number; unit?: ForceCode }, toUnit: ForceCode) {
    if (unit === toUnit) {
      return value;
    }
    /**
     * Convert imperial force [lbf] to metric force [N]
     */

    if (toUnit === ForceCode.NEWTON) {
      return round10(value / forceFactor);
    }

    /**
     * Convert metric force [N] to imperial force [lbf]
     */
    return round10(value * forceFactor);
  }

  static convertPressureUnit({ value, unit }: { value: number; unit?: PressureCode }, toUnit: PressureCode) {
    if (unit === toUnit) {
      return value;
    }
    /**
     * Convert imperial pressure [lbf/in^2] to metric pressure [MPa]
     */
    if (toUnit === PressureCode.MEGAPASCAL) {
      return round10(value / pressureFactor);
    }
    /**
     * Convert metric pressure [MPa] to imperial pressure [lbf/in^2]
     */
    return round10(value * pressureFactor);
  }

  static convertLengthUnit({ value, unit }: { value: number; unit?: LengthCode }, toUnit: LengthCode) {
    if (unit === toUnit) {
      return value;
    }
    /**
     * Convert imperial inches to metric mm
     */
    if (toUnit === LengthCode.MILLIMETER) {
      return round10(value / distanceFactor);
    }
    /**
     * Convert metric mm to imperial inches
     */
    return round10(value * distanceFactor);
  }

  static getProductionMethodName(procedure: IbhMaterialResponseDtoProcedureEnum) {
    switch (procedure) {
      case IbhMaterialResponseDtoProcedureEnum.CNC:
        return 'Mechanical Reprocessing';
      case IbhMaterialResponseDtoProcedureEnum.COATING:
        return 'Three Dimensional Print';
      case IbhMaterialResponseDtoProcedureEnum.DLP:
        return 'Three Dimensional Print';
      case IbhMaterialResponseDtoProcedureEnum.FDM:
        return 'Three Dimensional Print';
      case IbhMaterialResponseDtoProcedureEnum.IM:
        return 'Injection Molding';
      case IbhMaterialResponseDtoProcedureEnum.SLS:
        return 'Three Dimensional Print';
      case IbhMaterialResponseDtoProcedureEnum.WOUND:
        return 'Wound';
      default:
        return procedure;
    }
  }

  static getWidenImageUrl(widenShortKey: string | undefined, widenShortKeyIcon: string | null) {
    const key = widenShortKey ?? widenShortKeyIcon ?? '';
    if (key === '') {
      return null;
    }

    return `${WIDEN_CDN_IMAGE_BASE_URL}${key}`;
  }

  static async getBase64ImageFromURL(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = document.createElement('img');

      img.crossOrigin = 'Anonymous';
      img.src = url;

      img.onload = () => resolve(this.getBase64Image(img));
      img.onerror = error => reject(error);
    });
  }

  static getBase64Image(img: HTMLImageElement, type = 'image/png'): string {
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    canvas.getContext('2d').drawImage(img, 0, 0);
    return canvas.toDataURL(type).replace(new RegExp(`^data:${type};base64,`), '');
  }

  static getPiecesFromArticleNumberSlug(articleNumberSlug: string): {
    metricTypePiece: string;
    quantityPiece: string;
    dimensionsPiece: string;
  } {
    const allIndicatorsMatch = articleNumberSlug.match(/^([A-Z/\-0-9]{0,})_(INCH|MM){1,}_(q[0-9]{1,})_([\w:.-]+){1}$/);
    const unitAndQuantityIndicatorsMatch = articleNumberSlug.match(/^([A-Z/\-0-9]{0,})_(INCH|MM){1,}_(q[0-9]{1,})$/);
    const unitAndDimensionIndicatorsMatch = articleNumberSlug.match(/^([A-Z/\-0-9]{0,})_(INCH|MM){1,}_([\w:.-]+){1}$/);
    const unitIndicatorMatch = articleNumberSlug.match(/^([A-Z/\-0-9]{0,})_(INCH|MM){1,}$/);
    const quantityAndDimensionIndicatorsMatch = articleNumberSlug.match(
      /^([A-Z/\-0-9]{0,})_(q[0-9]{1,})_([\w:.-]+){1}$/
    );
    const quantityIndicatorMatch = articleNumberSlug.match(/^([A-Z/\-0-9]{0,})_(q[0-9]{1,})$/);
    const dimensionIndicatorMatch = articleNumberSlug.match(/^([A-Z/\-0-9]{0,})_([\w:.-]+){1}$/);

    let metricTypePiece = 'MM';
    let quantityPiece = 'q1';
    let dimensionsPiece = '';
    if (allIndicatorsMatch) {
      [, , metricTypePiece, quantityPiece, dimensionsPiece] = allIndicatorsMatch;
    } else if (unitAndQuantityIndicatorsMatch) {
      [, , metricTypePiece, quantityPiece] = unitAndQuantityIndicatorsMatch;
    } else if (unitAndDimensionIndicatorsMatch) {
      [, , metricTypePiece, dimensionsPiece] = unitAndDimensionIndicatorsMatch;
    } else if (unitIndicatorMatch) {
      [, , metricTypePiece] = unitIndicatorMatch;
    } else if (quantityAndDimensionIndicatorsMatch) {
      [, , quantityPiece, dimensionsPiece] = quantityAndDimensionIndicatorsMatch;
    } else if (quantityIndicatorMatch) {
      [, , quantityPiece] = quantityIndicatorMatch;
    } else if (dimensionIndicatorMatch) {
      [, , dimensionsPiece] = dimensionIndicatorMatch;
    }

    return {
      metricTypePiece,
      quantityPiece,
      dimensionsPiece,
    };
  }

  static getResourceWithFallback = (i18n: i18n, locale: string, namespace: string, key: string): string | undefined => {
    if (!i18n) {
      return undefined;
    }

    const localesToTry = [locale, ...(fallbackLngConfig[locale] ?? [defaultLocale])];

    for (const currentLocale of localesToTry) {
      const value = i18n.getResource(currentLocale, namespace, key) as string | undefined;

      if (value != null) {
        return value;
      }
    }

    return undefined;
  };

  static getDimensionsDescriptionMap(
    shapeKey: string,
    i18nObj: i18n,
    locale: string
  ): {
    [key: string]: string;
  } {
    const result: {
      [key: string]: string;
    } = {};
    Object.keys(dimensionKeys).forEach(dimensionKey => {
      const dimensionsLabelKey = `TECHNICAL_DATA_LABELS.DIMENSIONS.${shapeKey}.${(dimensionKeys[dimensionKey] as string).toUpperCase()}`;

      const label = this.getResourceWithFallback(i18nObj, locale, 'bearing-hub/bearingHub', dimensionsLabelKey) ?? '';

      if (label) {
        result[dimensionKeys[dimensionKey] as string] = label;
      }
    });

    return result;
  }

  static getBevelsDescriptionMap(
    i18nObj: i18n,
    locale: string
  ): {
    [key: string]: string;
  } {
    const result: {
      [key: string]: string;
    } = {};
    Object.keys(bevelKeys).forEach(bevelKey => {
      const bevelLabelKey = `TECHNICAL_DATA_LABELS.BEVELS.${bevelKey.toUpperCase().replace(/\./g, '')}`;

      const label = this.getResourceWithFallback(i18nObj, locale, 'bearing-hub/bearingHub', bevelLabelKey) ?? '';

      if (label) {
        result[bevelKey] = label;
      }
    });

    return result;
  }

  static getMainIbhInfoPagePath(i18nextLocale: string): string {
    const localeObj = new Locale(i18nextLocale);
    return (
      MAIN_IBH_INFO_PAGE_PATH[localeObj.baseName as MAIN_IBH_INFO_PAGE_PATH_LOCALES] || MAIN_IBH_INFO_PAGE_PATH.default
    );
  }
}

/* eslint-enable @typescript-eslint/no-unsafe-member-access */
